export class GeoJSONFormat {
  static read(data: string | object): GeoJSON.FeatureCollection {
    let parsed: any;
    if (typeof data === 'string') {
      parsed = JSON.parse(data);
    } else {
      parsed = data;
    }

    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid GeoJSON: input must be a valid JSON object');
    }

    if (parsed.type === 'FeatureCollection') {
      if (!Array.isArray(parsed.features)) {
        throw new Error('Invalid GeoJSON FeatureCollection: missing features array');
      }
      return parsed as GeoJSON.FeatureCollection;
    }

    if (parsed.type === 'Feature') {
      return {
        type: 'FeatureCollection',
        features: [parsed as GeoJSON.Feature],
      };
    }

    const validGeometryTypes = [
      'Point', 'MultiPoint', 'LineString', 'MultiLineString',
      'Polygon', 'MultiPolygon', 'GeometryCollection',
    ];
    if (validGeometryTypes.includes(parsed.type)) {
      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: parsed as GeoJSON.Geometry,
          properties: {},
        }],
      };
    }

    throw new Error(`Invalid GeoJSON: unrecognized type "${parsed.type}"`);
  }

  static write(features: GeoJSON.FeatureCollection, options?: { precision?: number }): string {
    if (options?.precision !== undefined) {
      const rounded = JSON.parse(JSON.stringify(features));
      GeoJSONFormat._roundCoordinates(rounded, options.precision);
      return JSON.stringify(rounded);
    }
    return JSON.stringify(features);
  }

  private static _roundCoordinates(obj: any, precision: number): void {
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        if (typeof obj[i] === 'number') {
          obj[i] = parseFloat(obj[i].toFixed(precision));
        } else {
          GeoJSONFormat._roundCoordinates(obj[i], precision);
        }
      }
    } else if (obj && typeof obj === 'object') {
      if (obj.coordinates) {
        GeoJSONFormat._roundCoordinates(obj.coordinates, precision);
      }
      if (obj.geometry) {
        GeoJSONFormat._roundCoordinates(obj.geometry, precision);
      }
      if (obj.features) {
        GeoJSONFormat._roundCoordinates(obj.features, precision);
      }
      if (obj.geometries) {
        GeoJSONFormat._roundCoordinates(obj.geometries, precision);
      }
    }
  }
}

export class KMLFormat {
  static read(data: string): GeoJSON.FeatureCollection {
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, 'application/xml');
    const features: GeoJSON.Feature[] = [];

    const placemarks = doc.getElementsByTagName('Placemark');
    for (let i = 0; i < placemarks.length; i++) {
      const placemark = placemarks[i];
      const properties: Record<string, any> = {};

      const nameEl = placemark.getElementsByTagName('name')[0];
      if (nameEl?.textContent) {
        properties.name = nameEl.textContent;
      }

      const descEl = placemark.getElementsByTagName('description')[0];
      if (descEl?.textContent) {
        properties.description = descEl.textContent;
      }

      const extData = placemark.getElementsByTagName('ExtendedData')[0];
      if (extData) {
        const dataEls = extData.getElementsByTagName('Data');
        for (let d = 0; d < dataEls.length; d++) {
          const key = dataEls[d].getAttribute('name');
          const valueEl = dataEls[d].getElementsByTagName('value')[0];
          if (key && valueEl?.textContent) {
            properties[key] = valueEl.textContent;
          }
        }
      }

      const geometry = KMLFormat._parseGeometry(placemark);
      if (geometry) {
        features.push({ type: 'Feature', geometry, properties });
      }
    }

    return { type: 'FeatureCollection', features };
  }

  private static _parseGeometry(element: Element): GeoJSON.Geometry | null {
    const point = element.getElementsByTagName('Point')[0];
    if (point) {
      const coords = KMLFormat._parseCoordinateString(
        point.getElementsByTagName('coordinates')[0]?.textContent || ''
      );
      if (coords.length > 0) {
        return { type: 'Point', coordinates: coords[0] };
      }
    }

    const lineString = element.getElementsByTagName('LineString')[0];
    if (lineString) {
      const coords = KMLFormat._parseCoordinateString(
        lineString.getElementsByTagName('coordinates')[0]?.textContent || ''
      );
      return { type: 'LineString', coordinates: coords };
    }

    const polygon = element.getElementsByTagName('Polygon')[0];
    if (polygon) {
      const rings: number[][][] = [];
      const outerBoundary = polygon.getElementsByTagName('outerBoundaryIs')[0];
      if (outerBoundary) {
        const coords = KMLFormat._parseCoordinateString(
          outerBoundary.getElementsByTagName('coordinates')[0]?.textContent || ''
        );
        rings.push(coords);
      }
      const innerBoundaries = polygon.getElementsByTagName('innerBoundaryIs');
      for (let i = 0; i < innerBoundaries.length; i++) {
        const coords = KMLFormat._parseCoordinateString(
          innerBoundaries[i].getElementsByTagName('coordinates')[0]?.textContent || ''
        );
        rings.push(coords);
      }
      return { type: 'Polygon', coordinates: rings };
    }

    const multiGeom = element.getElementsByTagName('MultiGeometry')[0];
    if (multiGeom) {
      const geometries: GeoJSON.Geometry[] = [];
      for (let i = 0; i < multiGeom.children.length; i++) {
        const child = multiGeom.children[i];
        const wrapper = document.createElement('div');
        wrapper.appendChild(child.cloneNode(true));
        const geom = KMLFormat._parseGeometry(wrapper);
        if (geom) geometries.push(geom);
      }
      if (geometries.length > 0) {
        return { type: 'GeometryCollection', geometries };
      }
    }

    return null;
  }

  private static _parseCoordinateString(coordStr: string): number[][] {
    return coordStr
      .trim()
      .split(/\s+/)
      .filter(s => s.length > 0)
      .map(tuple => {
        const parts = tuple.split(',').map(Number);
        if (parts.length >= 3) {
          return [parts[0], parts[1], parts[2]];
        }
        return [parts[0], parts[1]];
      });
  }

  static write(features: GeoJSON.FeatureCollection, options?: { name?: string; description?: string }): string {
    let kml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    kml += '<kml xmlns="http://www.opengis.net/kml/2.2">\n';
    kml += '<Document>\n';

    if (options?.name) {
      kml += `  <name>${KMLFormat._escapeXml(options.name)}</name>\n`;
    }
    if (options?.description) {
      kml += `  <description>${KMLFormat._escapeXml(options.description)}</description>\n`;
    }

    for (const feature of features.features) {
      kml += '  <Placemark>\n';
      if (feature.properties?.name) {
        kml += `    <name>${KMLFormat._escapeXml(feature.properties.name)}</name>\n`;
      }
      if (feature.properties?.description) {
        kml += `    <description>${KMLFormat._escapeXml(feature.properties.description)}</description>\n`;
      }
      if (feature.geometry) {
        kml += KMLFormat._writeGeometry(feature.geometry, '    ');
      }
      kml += '  </Placemark>\n';
    }

    kml += '</Document>\n';
    kml += '</kml>';
    return kml;
  }

  private static _writeGeometry(geometry: GeoJSON.Geometry, indent: string): string {
    switch (geometry.type) {
      case 'Point':
        return `${indent}<Point>\n${indent}  <coordinates>${geometry.coordinates.join(',')}</coordinates>\n${indent}</Point>\n`;
      case 'LineString':
        return `${indent}<LineString>\n${indent}  <coordinates>${geometry.coordinates.map(c => c.join(',')).join(' ')}</coordinates>\n${indent}</LineString>\n`;
      case 'Polygon':
        let polyKml = `${indent}<Polygon>\n`;
        geometry.coordinates.forEach((ring, i) => {
          const tag = i === 0 ? 'outerBoundaryIs' : 'innerBoundaryIs';
          polyKml += `${indent}  <${tag}>\n${indent}    <LinearRing>\n${indent}      <coordinates>${ring.map(c => c.join(',')).join(' ')}</coordinates>\n${indent}    </LinearRing>\n${indent}  </${tag}>\n`;
        });
        polyKml += `${indent}</Polygon>\n`;
        return polyKml;
      case 'GeometryCollection':
        let multiKml = `${indent}<MultiGeometry>\n`;
        for (const geom of geometry.geometries) {
          multiKml += KMLFormat._writeGeometry(geom, indent + '  ');
        }
        multiKml += `${indent}</MultiGeometry>\n`;
        return multiKml;
      case 'MultiPoint':
        let mpKml = `${indent}<MultiGeometry>\n`;
        for (const coord of geometry.coordinates) {
          mpKml += `${indent}  <Point>\n${indent}    <coordinates>${coord.join(',')}</coordinates>\n${indent}  </Point>\n`;
        }
        mpKml += `${indent}</MultiGeometry>\n`;
        return mpKml;
      case 'MultiLineString':
        let mlKml = `${indent}<MultiGeometry>\n`;
        for (const line of geometry.coordinates) {
          mlKml += `${indent}  <LineString>\n${indent}    <coordinates>${line.map(c => c.join(',')).join(' ')}</coordinates>\n${indent}  </LineString>\n`;
        }
        mlKml += `${indent}</MultiGeometry>\n`;
        return mlKml;
      case 'MultiPolygon':
        let mplKml = `${indent}<MultiGeometry>\n`;
        for (const polygon of geometry.coordinates) {
          mplKml += KMLFormat._writeGeometry({ type: 'Polygon', coordinates: polygon }, indent + '  ');
        }
        mplKml += `${indent}</MultiGeometry>\n`;
        return mplKml;
      default:
        return '';
    }
  }

  private static _escapeXml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}

export class GPXFormat {
  static read(data: string): GeoJSON.FeatureCollection {
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, 'application/xml');
    const features: GeoJSON.Feature[] = [];

    const wpts = doc.getElementsByTagName('wpt');
    for (let i = 0; i < wpts.length; i++) {
      const wpt = wpts[i];
      const lat = parseFloat(wpt.getAttribute('lat') || '0');
      const lon = parseFloat(wpt.getAttribute('lon') || '0');
      const ele = wpt.getElementsByTagName('ele')[0];
      const nameEl = wpt.getElementsByTagName('name')[0];
      const descEl = wpt.getElementsByTagName('desc')[0];
      const timeEl = wpt.getElementsByTagName('time')[0];

      const coordinates: number[] = [lon, lat];
      if (ele?.textContent) coordinates.push(parseFloat(ele.textContent));

      const properties: Record<string, any> = { _gpxType: 'wpt' };
      if (nameEl?.textContent) properties.name = nameEl.textContent;
      if (descEl?.textContent) properties.desc = descEl.textContent;
      if (timeEl?.textContent) properties.time = timeEl.textContent;

      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates },
        properties,
      });
    }

    const rtes = doc.getElementsByTagName('rte');
    for (let i = 0; i < rtes.length; i++) {
      const rte = rtes[i];
      const nameEl = rte.getElementsByTagName('name')[0];
      const descEl = rte.getElementsByTagName('desc')[0];
      const rtepts = rte.getElementsByTagName('rtept');
      const coordinates: number[][] = [];

      for (let j = 0; j < rtepts.length; j++) {
        const pt = rtepts[j];
        const lat = parseFloat(pt.getAttribute('lat') || '0');
        const lon = parseFloat(pt.getAttribute('lon') || '0');
        const ele = pt.getElementsByTagName('ele')[0];
        const coord: number[] = [lon, lat];
        if (ele?.textContent) coord.push(parseFloat(ele.textContent));
        coordinates.push(coord);
      }

      const properties: Record<string, any> = { _gpxType: 'rte' };
      if (nameEl?.textContent) properties.name = nameEl.textContent;
      if (descEl?.textContent) properties.desc = descEl.textContent;

      if (coordinates.length > 0) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates },
          properties,
        });
      }
    }

    const trks = doc.getElementsByTagName('trk');
    for (let i = 0; i < trks.length; i++) {
      const trk = trks[i];
      const nameEl = trk.getElementsByTagName('name')[0];
      const descEl = trk.getElementsByTagName('desc')[0];
      const trksegs = trk.getElementsByTagName('trkseg');

      const properties: Record<string, any> = { _gpxType: 'trk' };
      if (nameEl?.textContent) properties.name = nameEl.textContent;
      if (descEl?.textContent) properties.desc = descEl.textContent;

      if (trksegs.length === 1) {
        const trkpts = trksegs[0].getElementsByTagName('trkpt');
        const coordinates: number[][] = [];
        for (let j = 0; j < trkpts.length; j++) {
          const pt = trkpts[j];
          const lat = parseFloat(pt.getAttribute('lat') || '0');
          const lon = parseFloat(pt.getAttribute('lon') || '0');
          const ele = pt.getElementsByTagName('ele')[0];
          const coord: number[] = [lon, lat];
          if (ele?.textContent) coord.push(parseFloat(ele.textContent));
          coordinates.push(coord);
        }
        if (coordinates.length > 0) {
          features.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates },
            properties,
          });
        }
      } else if (trksegs.length > 1) {
        const lines: number[][][] = [];
        for (let s = 0; s < trksegs.length; s++) {
          const trkpts = trksegs[s].getElementsByTagName('trkpt');
          const coordinates: number[][] = [];
          for (let j = 0; j < trkpts.length; j++) {
            const pt = trkpts[j];
            const lat = parseFloat(pt.getAttribute('lat') || '0');
            const lon = parseFloat(pt.getAttribute('lon') || '0');
            const ele = pt.getElementsByTagName('ele')[0];
            const coord: number[] = [lon, lat];
            if (ele?.textContent) coord.push(parseFloat(ele.textContent));
            coordinates.push(coord);
          }
          if (coordinates.length > 0) lines.push(coordinates);
        }
        if (lines.length > 0) {
          features.push({
            type: 'Feature',
            geometry: { type: 'MultiLineString', coordinates: lines },
            properties,
          });
        }
      }
    }

    return { type: 'FeatureCollection', features };
  }

  static write(features: GeoJSON.FeatureCollection, options?: { creator?: string }): string {
    const creator = options?.creator || 'MIGIS SDK';
    let gpx = '<?xml version="1.0" encoding="UTF-8"?>\n';
    gpx += `<gpx version="1.1" creator="${GPXFormat._escapeXml(creator)}" xmlns="http://www.topografix.com/GPX/1/1">\n`;

    for (const feature of features.features) {
      const geom = feature.geometry;
      const props = feature.properties || {};

      if (geom.type === 'Point') {
        gpx += `  <wpt lat="${geom.coordinates[1]}" lon="${geom.coordinates[0]}">\n`;
        if (geom.coordinates.length > 2) {
          gpx += `    <ele>${geom.coordinates[2]}</ele>\n`;
        }
        if (props.name) gpx += `    <name>${GPXFormat._escapeXml(props.name)}</name>\n`;
        if (props.desc) gpx += `    <desc>${GPXFormat._escapeXml(props.desc)}</desc>\n`;
        if (props.time) gpx += `    <time>${props.time}</time>\n`;
        gpx += '  </wpt>\n';
      } else if (geom.type === 'LineString') {
        const gpxType = props._gpxType || 'trk';
        if (gpxType === 'rte') {
          gpx += '  <rte>\n';
          if (props.name) gpx += `    <name>${GPXFormat._escapeXml(props.name)}</name>\n`;
          if (props.desc) gpx += `    <desc>${GPXFormat._escapeXml(props.desc)}</desc>\n`;
          for (const coord of geom.coordinates) {
            gpx += `    <rtept lat="${coord[1]}" lon="${coord[0]}">\n`;
            if (coord.length > 2) gpx += `      <ele>${coord[2]}</ele>\n`;
            gpx += '    </rtept>\n';
          }
          gpx += '  </rte>\n';
        } else {
          gpx += '  <trk>\n';
          if (props.name) gpx += `    <name>${GPXFormat._escapeXml(props.name)}</name>\n`;
          if (props.desc) gpx += `    <desc>${GPXFormat._escapeXml(props.desc)}</desc>\n`;
          gpx += '    <trkseg>\n';
          for (const coord of geom.coordinates) {
            gpx += `      <trkpt lat="${coord[1]}" lon="${coord[0]}">\n`;
            if (coord.length > 2) gpx += `        <ele>${coord[2]}</ele>\n`;
            gpx += '      </trkpt>\n';
          }
          gpx += '    </trkseg>\n';
          gpx += '  </trk>\n';
        }
      } else if (geom.type === 'MultiLineString') {
        gpx += '  <trk>\n';
        if (props.name) gpx += `    <name>${GPXFormat._escapeXml(props.name)}</name>\n`;
        if (props.desc) gpx += `    <desc>${GPXFormat._escapeXml(props.desc)}</desc>\n`;
        for (const line of geom.coordinates) {
          gpx += '    <trkseg>\n';
          for (const coord of line) {
            gpx += `      <trkpt lat="${coord[1]}" lon="${coord[0]}">\n`;
            if (coord.length > 2) gpx += `        <ele>${coord[2]}</ele>\n`;
            gpx += '      </trkpt>\n';
          }
          gpx += '    </trkseg>\n';
        }
        gpx += '  </trk>\n';
      }
    }

    gpx += '</gpx>';
    return gpx;
  }

  private static _escapeXml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}

export class WKTFormat {
  static read(wkt: string): GeoJSON.Geometry {
    const trimmed = wkt.trim();
    const upperWkt = trimmed.toUpperCase();

    if (upperWkt.startsWith('GEOMETRYCOLLECTION')) {
      return WKTFormat._parseGeometryCollection(trimmed);
    }
    if (upperWkt.startsWith('MULTIPOLYGON')) {
      return WKTFormat._parseMultiPolygon(trimmed);
    }
    if (upperWkt.startsWith('MULTILINESTRING')) {
      return WKTFormat._parseMultiLineString(trimmed);
    }
    if (upperWkt.startsWith('MULTIPOINT')) {
      return WKTFormat._parseMultiPoint(trimmed);
    }
    if (upperWkt.startsWith('POLYGON')) {
      return WKTFormat._parsePolygon(trimmed);
    }
    if (upperWkt.startsWith('LINESTRING')) {
      return WKTFormat._parseLineString(trimmed);
    }
    if (upperWkt.startsWith('POINT')) {
      return WKTFormat._parsePoint(trimmed);
    }

    throw new Error(`Unsupported WKT geometry type: ${trimmed.split('(')[0].trim()}`);
  }

  private static _extractParenContent(wkt: string): string {
    const firstParen = wkt.indexOf('(');
    const lastParen = wkt.lastIndexOf(')');
    if (firstParen === -1 || lastParen === -1) {
      throw new Error('Invalid WKT: missing parentheses');
    }
    return wkt.substring(firstParen + 1, lastParen);
  }

  private static _parseCoord(str: string): number[] {
    return str.trim().split(/\s+/).map(Number);
  }

  private static _parseCoordList(str: string): number[][] {
    return str.split(',').map(s => WKTFormat._parseCoord(s));
  }

  private static _parsePoint(wkt: string): GeoJSON.Point {
    const content = WKTFormat._extractParenContent(wkt);
    return { type: 'Point', coordinates: WKTFormat._parseCoord(content) };
  }

  private static _parseLineString(wkt: string): GeoJSON.LineString {
    const content = WKTFormat._extractParenContent(wkt);
    return { type: 'LineString', coordinates: WKTFormat._parseCoordList(content) };
  }

  private static _parsePolygon(wkt: string): GeoJSON.Polygon {
    const content = WKTFormat._extractParenContent(wkt);
    const rings = WKTFormat._splitRings(content);
    return {
      type: 'Polygon',
      coordinates: rings.map(r => WKTFormat._parseCoordList(r)),
    };
  }

  private static _parseMultiPoint(wkt: string): GeoJSON.MultiPoint {
    const content = WKTFormat._extractParenContent(wkt);
    const cleaned = content.replace(/\(/g, '').replace(/\)/g, '');
    return {
      type: 'MultiPoint',
      coordinates: WKTFormat._parseCoordList(cleaned),
    };
  }

  private static _parseMultiLineString(wkt: string): GeoJSON.MultiLineString {
    const content = WKTFormat._extractParenContent(wkt);
    const parts = WKTFormat._splitRings(content);
    return {
      type: 'MultiLineString',
      coordinates: parts.map(p => WKTFormat._parseCoordList(p)),
    };
  }

  private static _parseMultiPolygon(wkt: string): GeoJSON.MultiPolygon {
    const content = WKTFormat._extractParenContent(wkt);
    const polygons: number[][][][] = [];
    const polyStrings = WKTFormat._splitTopLevel(content);
    for (const polyStr of polyStrings) {
      const inner = WKTFormat._extractParenContent('X(' + polyStr + ')');
      const rings = WKTFormat._splitRings(inner);
      polygons.push(rings.map(r => WKTFormat._parseCoordList(r)));
    }
    return { type: 'MultiPolygon', coordinates: polygons };
  }

  private static _parseGeometryCollection(wkt: string): GeoJSON.GeometryCollection {
    const content = WKTFormat._extractParenContent(wkt);
    const geometries: GeoJSON.Geometry[] = [];
    let depth = 0;
    let current = '';

    for (let i = 0; i < content.length; i++) {
      const ch = content[i];
      if (ch === '(') depth++;
      if (ch === ')') depth--;
      if (ch === ',' && depth === 0) {
        geometries.push(WKTFormat.read(current.trim()));
        current = '';
      } else {
        current += ch;
      }
    }
    if (current.trim()) {
      geometries.push(WKTFormat.read(current.trim()));
    }

    return { type: 'GeometryCollection', geometries };
  }

  private static _splitRings(content: string): string[] {
    const results: string[] = [];
    let depth = 0;
    let current = '';
    for (let i = 0; i < content.length; i++) {
      const ch = content[i];
      if (ch === '(') {
        depth++;
        if (depth === 1) continue;
      }
      if (ch === ')') {
        depth--;
        if (depth === 0) {
          results.push(current);
          current = '';
          continue;
        }
      }
      if (ch === ',' && depth === 0) continue;
      current += ch;
    }
    if (current.trim()) results.push(current);
    return results;
  }

  private static _splitTopLevel(content: string): string[] {
    const results: string[] = [];
    let depth = 0;
    let current = '';
    for (let i = 0; i < content.length; i++) {
      const ch = content[i];
      if (ch === '(') {
        depth++;
        current += ch;
      } else if (ch === ')') {
        depth--;
        current += ch;
      } else if (ch === ',' && depth === 0) {
        results.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    if (current.trim()) results.push(current.trim());
    return results;
  }

  static write(geometry: GeoJSON.Geometry): string {
    switch (geometry.type) {
      case 'Point':
        return `POINT (${geometry.coordinates.join(' ')})`;
      case 'LineString':
        return `LINESTRING (${geometry.coordinates.map(c => c.join(' ')).join(', ')})`;
      case 'Polygon':
        return `POLYGON (${geometry.coordinates.map(ring => '(' + ring.map(c => c.join(' ')).join(', ') + ')').join(', ')})`;
      case 'MultiPoint':
        return `MULTIPOINT (${geometry.coordinates.map(c => '(' + c.join(' ') + ')').join(', ')})`;
      case 'MultiLineString':
        return `MULTILINESTRING (${geometry.coordinates.map(line => '(' + line.map(c => c.join(' ')).join(', ') + ')').join(', ')})`;
      case 'MultiPolygon':
        return `MULTIPOLYGON (${geometry.coordinates.map(poly => '(' + poly.map(ring => '(' + ring.map(c => c.join(' ')).join(', ') + ')').join(', ') + ')').join(', ')})`;
      case 'GeometryCollection':
        return `GEOMETRYCOLLECTION (${geometry.geometries.map(g => WKTFormat.write(g)).join(', ')})`;
      default:
        throw new Error(`Unsupported geometry type for WKT: ${(geometry as any).type}`);
    }
  }
}

interface TopoJSONTopology {
  type: 'Topology';
  objects: Record<string, TopoJSONGeometryCollection>;
  arcs: number[][][];
  transform?: {
    scale: [number, number];
    translate: [number, number];
  };
}

interface TopoJSONGeometryCollection {
  type: string;
  geometries?: TopoJSONGeometry[];
}

interface TopoJSONGeometry {
  type: string;
  arcs?: any;
  coordinates?: any;
  properties?: Record<string, any>;
  id?: string | number;
}

export class TopoJSONFormat {
  static read(data: string | object): GeoJSON.FeatureCollection {
    let topology: TopoJSONTopology;
    if (typeof data === 'string') {
      topology = JSON.parse(data);
    } else {
      topology = data as TopoJSONTopology;
    }

    if (topology.type !== 'Topology') {
      throw new Error('Invalid TopoJSON: missing Topology type');
    }

    const features: GeoJSON.Feature[] = [];
    const arcs = TopoJSONFormat._decodeArcs(topology);

    for (const key of Object.keys(topology.objects)) {
      const obj = topology.objects[key];
      if (obj.geometries) {
        for (const geom of obj.geometries) {
          const geometry = TopoJSONFormat._convertGeometry(geom, arcs);
          if (geometry) {
            features.push({
              type: 'Feature',
              geometry,
              properties: geom.properties || {},
              ...(geom.id !== undefined ? { id: geom.id } : {}),
            });
          }
        }
      }
    }

    return { type: 'FeatureCollection', features };
  }

  private static _decodeArcs(topology: TopoJSONTopology): number[][][] {
    const transform = topology.transform;
    if (!transform) return topology.arcs;

    const { scale, translate } = transform;
    return topology.arcs.map(arc => {
      let x = 0;
      let y = 0;
      return arc.map(point => {
        x += point[0];
        y += point[1];
        return [
          x * scale[0] + translate[0],
          y * scale[1] + translate[1],
        ];
      });
    });
  }

  private static _arcToCoords(arcIndex: number, arcs: number[][][]): number[][] {
    if (arcIndex >= 0) {
      return arcs[arcIndex].slice();
    }
    return arcs[~arcIndex].slice().reverse();
  }

  private static _stitchArcs(arcIndices: number[], arcs: number[][][]): number[][] {
    const coords: number[][] = [];
    for (let i = 0; i < arcIndices.length; i++) {
      const arcCoords = TopoJSONFormat._arcToCoords(arcIndices[i], arcs);
      if (i > 0) arcCoords.shift();
      coords.push(...arcCoords);
    }
    return coords;
  }

  private static _convertGeometry(geom: TopoJSONGeometry, arcs: number[][][]): GeoJSON.Geometry | null {
    switch (geom.type) {
      case 'Point':
        return { type: 'Point', coordinates: geom.coordinates };
      case 'MultiPoint':
        return { type: 'MultiPoint', coordinates: geom.coordinates };
      case 'LineString':
        return { type: 'LineString', coordinates: TopoJSONFormat._stitchArcs(geom.arcs, arcs) };
      case 'MultiLineString':
        return {
          type: 'MultiLineString',
          coordinates: geom.arcs.map((a: number[]) => TopoJSONFormat._stitchArcs(a, arcs)),
        };
      case 'Polygon':
        return {
          type: 'Polygon',
          coordinates: geom.arcs.map((ring: number[]) => TopoJSONFormat._stitchArcs(ring, arcs)),
        };
      case 'MultiPolygon':
        return {
          type: 'MultiPolygon',
          coordinates: geom.arcs.map((poly: number[][]) =>
            poly.map((ring: number[]) => TopoJSONFormat._stitchArcs(ring, arcs))
          ),
        };
      case 'GeometryCollection':
        if (geom.arcs) {
          return null;
        }
        return null;
      default:
        return null;
    }
  }
}

export class CSVFormat {
  static read(
    data: string,
    options?: { latField?: string; lonField?: string; delimiter?: string }
  ): GeoJSON.FeatureCollection {
    const latField = options?.latField || 'lat';
    const lonField = options?.lonField || 'lon';
    const delimiter = options?.delimiter || ',';

    const lines = data.trim().split(/\r?\n/);
    if (lines.length < 2) {
      return { type: 'FeatureCollection', features: [] };
    }

    const headers = CSVFormat._parseLine(lines[0], delimiter);
    const latIdx = headers.findIndex(h => h.toLowerCase() === latField.toLowerCase());
    const lonIdx = headers.findIndex(h => h.toLowerCase() === lonField.toLowerCase());

    if (latIdx === -1 || lonIdx === -1) {
      const latAliases = ['lat', 'latitude', 'y'];
      const lonAliases = ['lon', 'lng', 'long', 'longitude', 'x'];
      const foundLat = latIdx !== -1 ? latIdx : headers.findIndex(h => latAliases.includes(h.toLowerCase()));
      const foundLon = lonIdx !== -1 ? lonIdx : headers.findIndex(h => lonAliases.includes(h.toLowerCase()));

      if (foundLat === -1 || foundLon === -1) {
        throw new Error(`Could not find lat/lon fields in CSV headers: ${headers.join(', ')}`);
      }

      return CSVFormat._parseWithIndices(lines, headers, foundLat, foundLon, delimiter);
    }

    return CSVFormat._parseWithIndices(lines, headers, latIdx, lonIdx, delimiter);
  }

  private static _parseWithIndices(
    lines: string[],
    headers: string[],
    latIdx: number,
    lonIdx: number,
    delimiter: string
  ): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = CSVFormat._parseLine(line, delimiter);
      const lat = parseFloat(values[latIdx]);
      const lon = parseFloat(values[lonIdx]);

      if (isNaN(lat) || isNaN(lon)) continue;

      const properties: Record<string, any> = {};
      for (let j = 0; j < headers.length; j++) {
        if (j !== latIdx && j !== lonIdx) {
          const val = values[j] || '';
          const num = parseFloat(val);
          properties[headers[j]] = !isNaN(num) && val.trim() !== '' ? num : val;
        }
      }

      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lon, lat] },
        properties,
      });
    }

    return { type: 'FeatureCollection', features };
  }

  private static _parseLine(line: string, delimiter: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === delimiter) {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
    }
    result.push(current.trim());
    return result;
  }

  static write(
    features: GeoJSON.FeatureCollection,
    options?: { latField?: string; lonField?: string; delimiter?: string }
  ): string {
    const latField = options?.latField || 'lat';
    const lonField = options?.lonField || 'lon';
    const delimiter = options?.delimiter || ',';

    const allKeys = new Set<string>();
    for (const feature of features.features) {
      if (feature.properties) {
        Object.keys(feature.properties).forEach(k => allKeys.add(k));
      }
    }

    const propKeys = Array.from(allKeys);
    const headers = [lonField, latField, ...propKeys];
    const lines: string[] = [headers.join(delimiter)];

    for (const feature of features.features) {
      if (feature.geometry?.type !== 'Point') continue;
      const coords = (feature.geometry as GeoJSON.Point).coordinates;
      const values: string[] = [
        String(coords[0]),
        String(coords[1]),
        ...propKeys.map(k => {
          const val = feature.properties?.[k];
          if (val === undefined || val === null) return '';
          const str = String(val);
          if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
            return '"' + str.replace(/"/g, '""') + '"';
          }
          return str;
        }),
      ];
      lines.push(values.join(delimiter));
    }

    return lines.join('\n');
  }
}
